import {isRight} from 'fp-ts/lib/Either'
import * as t from 'io-ts'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {Link} from 'react-router-dom'
import {
  JWKSCodec,
  JwtProfileMapCodec,
  NonEmptyString
} from '../../../common/types'
import {ERROR_CODE_NON_UNIQUE} from '../../../common/types/constants'
import {PreviewFlagMarker} from '../../../components/feature-flags'
import {CommaSeparatedListInput} from '../../../components/form'
import {JSONField} from '../../../components/library/json-field'
import {Button} from '../../../components/library/buttons/button'
import {Copyable} from '../../../components/library/copy'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {MultilineInput} from '../../../components/library/form/multiline-input'
import {TextInput} from '../../../components/library/form/text'
import {NumberInput} from '../../../components/library/form/number-input'
import {Icon} from '../../../components/library/icon/icon'
import {
  List,
  ListColumns,
  NoResultsPermissions
} from '../../../components/library/list/index'
import {useTheme} from 'styled-components'
import {Tag} from '../../../components/library/tag/tag'
import {Toggle} from '../../../components/library/toggles/toggle'
import {FormModal} from '../../../components/modals'
import {OverflowFooter} from '../../../components/overflow'
import {
  ClientKeyDetailFragment,
  useAddClientKeyMutation,
  useClientKeyListLazyQuery,
  useDeleteClientKeyMutation,
  useEditClientKeyMutation
} from '../../../data/workbench-api/generated/client-key'
import {
  ClientKeyType,
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {useFlags} from '../../../hooks/use-flags'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {
  DeleteLink,
  IconHeading,
  Mono,
  Section,
  SectionContent,
  SectionInfo
} from '../components'
import {API_KEY_DEFAULT_MAX_EXPIRY_DAYS} from '../../../common/types/constants'

const ValidPayloadCodec = t.intersection([
  t.type({
    environmentId: NonEmptyString,
    apiKey: NonEmptyString
  }),
  t.union([
    t.type({
      publicKey: NonEmptyString
    }),
    t.type({jwks: JWKSCodec})
  ]),
  t.partial({
    maxExpiryDays: t.union([t.number, t.null]),
    id: NonEmptyString,
    profileMapping: t.union([JwtProfileMapCodec, t.null]),
    issuerWhitelist: t.union([t.array(t.union([t.string, t.null])), t.null])
  })
])

type ValidPayload = t.TypeOf<typeof ValidPayloadCodec>

const clientKeyIsValid = (obj: Record<string, unknown>): obj is ValidPayload =>
  isRight(ValidPayloadCodec.decode(obj))

const Overflow = ({
  clientKey,
  closeDropdown,
  refetch,
  edit,
  canEdit
}: {
  clientKey: ClientKeyDetailFragment
  closeDropdown: () => void
  refetch: () => void
  edit: () => void
  canEdit: boolean
}) => {
  const [deleteMutation] = useDeleteClientKeyMutation()
  const [updateMutation] = useEditClientKeyMutation()

  return (
    <>
      <DropdownBody>
        <DropdownItem disabled={!canEdit}>
          <a
            onClick={() => {
              edit()
              closeDropdown()
            }}
          >
            Edit
          </a>
        </DropdownItem>

        <DropdownItem>
          <Link
            to={`../../testing-tools/validate-auth-token?key=${clientKey.id}`}
          >
            Test
          </Link>
        </DropdownItem>

        <DropdownItem disabled={!canEdit}>
          <a
            onClick={() => {
              updateMutation({
                variables: {
                  id: clientKey.id,
                  environmentId: clientKey.environmentId,
                  enabled: !clientKey.enabled
                }
              }).then(closeDropdown)
            }}
          >
            {clientKey.enabled ? 'Disable' : 'Enable'}
          </a>
        </DropdownItem>
        {!clientKey.enabled ? (
          <DropdownItem>
            <DeleteLink
              onClick={() => {
                if (confirm('Deletion is permanent - are you sure?')) {
                  deleteMutation({
                    variables: {
                      id: clientKey.id,
                      environmentId: clientKey.environmentId
                    }
                  }).then(() => {
                    refetch()
                    closeDropdown()
                  })
                }
              }}
            >
              Delete
            </DeleteLink>
          </DropdownItem>
        ) : null}
      </DropdownBody>
      <DropdownFooter>
        <OverflowFooter
          id={clientKey.id}
          created={clientKey.created}
          updated={clientKey.updated}
        />
      </DropdownFooter>
    </>
  )
}

type CurrentModal =
  | {
      type: 'new'
    }
  | {
      type: 'edit'
      clientKey: ClientKeyDetailFragment
    }

export const APIKeys = ({environment}: {environment: EnvironmentNode}) => {
  const [currentModal, setCurrentModal] = useState<CurrentModal | null>(null)
  const [error, setError] = useState<string | undefined>(undefined)
  const canEdit = environment.hasPermission(
    PermissionResource.ClientKey,
    PermissionAction.Edit
  )
  const canView = environment.hasPermission(
    PermissionResource.ClientKey,
    PermissionAction.View
  )
  const [query, {data, refetch = () => null, loading}] =
    useClientKeyListLazyQuery({
      variables: {
        environmentId: environment.id
      }
    })
  const clientKeys = data?.clientKeys?.nodes

  useEffect(() => {
    if (canView) {
      query()
    }
  }, [canView, query])

  const analytics = useAnalytics()

  useEffect(() => {
    analytics.track({
      event: 'Viewed sdk api keys configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>sdk>api-keys'
      }
    })
  }, [analytics, environment])

  const [addMutation] = useAddClientKeyMutation({
    onCompleted: () => {
      setCurrentModal(null)
      setError(undefined)
      refetch()
    },
    onError: err => setError(err.message)
  })

  const [editMutation] = useEditClientKeyMutation({
    onCompleted: () => {
      setCurrentModal(null)
      setError(undefined)
      refetch()
    },
    onError: err => setError(err.message)
  })

  const clientKeyColumns = useMemo(
    (): ListColumns<ClientKeyDetailFragment> => [
      {
        id: 'apiKey',
        header: 'API key',
        accessor: r => (
          <>
            {r.enabled ? (
              <Tag appearance="green">Active</Tag>
            ) : (
              <Tag appearance="light">Inactive</Tag>
            )}
            <a
              onClick={() => {
                setCurrentModal({type: 'edit', clientKey: r})
              }}
            >
              <Mono style={{marginRight: '5px', marginLeft: '10px'}}>
                {r.apiKey}
              </Mono>
            </a>
            <Copyable style={{width: '70px'}} value={r.apiKey} />
          </>
        )
      },
      {
        id: 'issuerWhitelist',
        header: 'Allowed issuers',
        horizontalAlign: 'right',
        accessor: r => r.issuerWhitelist?.join(', ')
      },
      {
        id: 'maxExpiryDays',
        header: 'Max expiry days',
        horizontalAlign: 'right',
        accessor: r => r.maxExpiryDays ?? API_KEY_DEFAULT_MAX_EXPIRY_DAYS
      }
    ],
    []
  )

  return (
    <Section>
      <Helmet>
        <title>SDK API keys</title>
      </Helmet>
      <header>
        <IconHeading>
          <Icon name="key" />
          SDK API keys
        </IconHeading>

        <nav>
          <Button
            onClick={() => setCurrentModal({type: 'new'})}
            disabled={!canEdit}
          >
            New key
          </Button>
        </nav>
      </header>
      <SectionInfo>
        <p>
          Create API keys for your SDK integrations, and configure allowed JWT
          issuers
        </p>
      </SectionInfo>
      <SectionContent>
        <List<ClientKeyDetailFragment>
          columns={clientKeyColumns}
          rows={clientKeys}
          loading={loading}
          bleed={'#f2f2f2'}
          renderOverflow={(clientKey, closeDropdown) => (
            <Overflow
              clientKey={clientKey}
              closeDropdown={closeDropdown}
              refetch={refetch}
              canEdit={canEdit}
              edit={() => setCurrentModal({type: 'edit', clientKey})}
            />
          )}
          renderEmpty={canView ? undefined : () => <NoResultsPermissions />}
        />
      </SectionContent>

      {currentModal ? (
        <ClientKeyModal
          environment={environment}
          handleClose={() => {
            setError(undefined)
            setCurrentModal(null)
          }}
          clientKey={
            // Pass the existing object or undefined if we are creating a new client key
            currentModal.type === 'edit' ? currentModal.clientKey : undefined
          }
          onSubmit={async variables => {
            // I want to set profileMapping to null if its an empty object
            const profileMapping =
              variables.profileMapping &&
              Object.keys(variables.profileMapping).length === 0
                ? null
                : variables.profileMapping

            if (variables.id) {
              const id = variables.id
              await editMutation({
                variables: {...variables, id, profileMapping}
              })
            } else {
              await addMutation({
                variables: {...variables, profileMapping}
              })
            }
          }}
          errorMessage={error}
        />
      ) : null}
    </Section>
  )
}

interface ClientKeyModalProps {
  environment: EnvironmentNode
  clientKey?: ClientKeyDetailFragment
  handleClose: () => void
  onSubmit: (variables: ValidPayload) => Promise<void>
  errorMessage?: string
}

type ClientKeyFormType = {
  environmentId: string
  id: string | undefined
} & Pick<
  ClientKeyDetailFragment,
  | 'enabled'
  | 'apiKey'
  | 'maxExpiryDays'
  | 'publicKey'
  | 'issuerWhitelist'
  | 'profileMapping'
  | 'jwks'
  | 'activeKeyType'
>

const ClientKeyModal = ({
  environment,
  handleClose,
  onSubmit,
  clientKey,
  errorMessage
}: ClientKeyModalProps) => {
  const flags = useFlags(environment.organisationId)
  const theme = useTheme()

  const [state, changeState, isChanged, resetState] =
    useStateChanged<ClientKeyFormType>({
      environmentId: environment.id,
      id: clientKey?.id,
      enabled: clientKey?.enabled,
      apiKey: clientKey?.apiKey ?? '',
      maxExpiryDays: clientKey?.maxExpiryDays,
      publicKey: clientKey?.publicKey,
      profileMapping: clientKey?.profileMapping,
      issuerWhitelist: clientKey?.issuerWhitelist,
      jwks: clientKey?.jwks,
      // default the active key type, one of: if provided (ie edit) use that, if new key and user has featurePreview JWKS, otherwise public key
      activeKeyType:
        clientKey?.activeKeyType !== undefined
          ? clientKey?.activeKeyType
          : flags.featurePreview
          ? ClientKeyType.Jwks
          : ClientKeyType.PublicKey
    })

  const [loading, setLoading] = useState<boolean>(false)
  const [validMapping, setValidMapping] = useState<boolean>(true)
  const [, setValidJWKS] = useState<boolean>(true)
  const canEdit = environment.hasPermission(
    PermissionResource.ClientKey,
    PermissionAction.Edit
  )

  const wrappedHandleClose = useCallback(() => {
    if (isChanged) {
      if (confirm('You have unsaved changes are you sure?')) {
        handleClose()
      }
    } else {
      handleClose()
    }
  }, [handleClose, isChanged])

  const displayError =
    errorMessage && errorMessage.includes(ERROR_CODE_NON_UNIQUE)
      ? 'Duplicate api key value'
      : errorMessage

  return (
    <FormModal
      style={{width: '740px'}}
      title={
        clientKey?.id ? `Client key ${clientKey?.apiKey}` : 'Add new client key'
      }
      handleClose={wrappedHandleClose}
      error={displayError}
      onSubmit={async () => {
        if (isChanged && canEdit && clientKeyIsValid(state)) {
          setLoading(true)
          await onSubmit(state).finally(() => {
            setLoading(false)
            resetState(s => s)
          })
        }
      }}
      submitText={clientKey?.id ? 'Save' : 'Add'}
      isValid={isChanged && clientKeyIsValid(state) && validMapping && !loading}
      readOnly={!canEdit}
    >
      {!clientKey?.id && ( // Client key is not editable, only show on create
        <TextInput
          style={{marginBottom: '30px', maxWidth: '100%'}}
          upLabel="API key"
          label="Enter API key..."
          value={state.apiKey}
          onChange={apiKey => changeState(c => ({...c, apiKey}))}
          readOnly={!canEdit}
        />
      )}
      <NumberInput
        style={{marginBottom: '30px', maxWidth: '100%'}}
        upLabel="Max expiry days"
        label="Enter Max expiry days..."
        value={state.maxExpiryDays ?? API_KEY_DEFAULT_MAX_EXPIRY_DAYS}
        onChange={maxExpiryDays => changeState(c => ({...c, maxExpiryDays}))}
        readOnly={!canEdit}
      />
      {!!flags.featurePreview && (
        <>
          <h4>
            Key type
            <PreviewFlagMarker />
          </h4>
          <Toggle
            style={{marginBottom: '30px', width: '100%'}}
            value={state.activeKeyType}
            options={[
              {value: ClientKeyType.Jwks, text: 'JWKS'},
              {value: ClientKeyType.PublicKey, text: 'Client key'}
            ]}
            onChange={(activeKeyType: ClientKeyType) =>
              changeState(c => ({...c, activeKeyType}))
            }
            legacyMode={true}
          />
        </>
      )}
      {state.activeKeyType === ClientKeyType.PublicKey && (
        <MultilineInput
          style={{maxWidth: '100%', marginBottom: '30px'}}
          name="publicKey"
          upLabel="Public key"
          label="Enter public key..."
          value={state.publicKey ?? ''}
          onChange={publicKey =>
            changeState(c => ({...c, publicKey: publicKey || null}))
          }
          readOnly={!canEdit}
        />
      )}
      {state.activeKeyType === ClientKeyType.Jwks && (
        <JSONField
          style={{height: '100px', maxWidth: '100%', marginBottom: '30px'}}
          label={<>JWKS Config</>}
          codec={JWKSCodec}
          raw={true}
          value={state.jwks ?? ''}
          onChange={jwks =>
            changeState(c => ({
              ...c,
              jwks: jwks ?? null
            }))
          }
          setValid={setValidJWKS}
          readOnly={!canEdit}
          legacyMode={true}
        />
      )}
      <CommaSeparatedListInput
        style={{maxWidth: '100%', marginBottom: '30px'}}
        label="Allowed JWT issuers (comma-separated)"
        value={String(state.issuerWhitelist || '')}
        onChange={issuerWhitelist =>
          changeState(c => ({...c, issuerWhitelist}))
        }
        readOnly={!canEdit}
      />
      <JSONField
        style={{height: '100px'}}
        label={
          <>
            Map JWT claims data to
            <Link
              to="../custom-fields"
              target="_blank"
              style={{color: theme.blue, marginLeft: '5px'}}
            >
              existing customer profile fields
            </Link>
          </>
        }
        codec={JwtProfileMapCodec}
        raw={true}
        value={state.profileMapping || {}}
        onChange={profileMapping => {
          changeState(c => ({
            ...c,
            profileMapping
          }))
        }}
        setValid={setValidMapping}
        readOnly={!canEdit}
      />
    </FormModal>
  )
}
